'use client'

// Üst bar sorunlu kargo bildirimi: müdahale listesine yeni giren sipariş, operatör hangi sayfada olursa olsun
// görünür (kırmızı hap + açılır liste) ve isteğe bağlı sesli uyarı verir. Liste tarayıcı başına dakikada bir
// çekilir (sekmeler localStorage üzerinden paylaşır); "yeni" kararı lib/cargo-problem-notify.ts'te.
// Kaynak veri cron'la (gündüz 10 dk) güncellenir. [[opop-cargo-shipments-sync]]

import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell, BellRing, Volume2, VolumeX } from 'lucide-react'
import { Button, Popover, PopoverContent, PopoverTrigger, Switch } from '@repo/ui'
import { getReadRoute } from '@/lib/read-route-client'
import type { CargoProblemAlertItem } from '@/lib/cargo-problem-alerts'
import {
  NOTIFY_POLL_MS,
  NOTIFY_SOUND_KEY,
  applyFetch,
  cargoProblemOrderHref,
  markSeen,
  notifyStorageKey,
  parseNotifyState,
  parseSoundPref,
  shouldFetch,
  unseenItems,
  type NotifyState,
} from '@/lib/cargo-problem-notify'

const MAX_LISTED = 20

type AudioCtor = typeof AudioContext

function audioCtor(): AudioCtor | null {
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext ?? null
}

/** İki kez "ding-dong" (ses dosyası gerekmez). */
function playChime(ctx: AudioContext) {
  const t0 = ctx.currentTime + 0.02
  const notes: [number, number][] = [[880, 0], [1318.5, 0.16], [880, 0.75], [1318.5, 0.91]]
  for (const [freq, at] of notes) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = freq
    gain.gain.setValueAtTime(0.0001, t0 + at)
    gain.gain.exponentialRampToValueAtTime(0.25, t0 + at + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.5)
    osc.connect(gain).connect(ctx.destination)
    osc.start(t0 + at)
    osc.stop(t0 + at + 0.55)
  }
}

export function CargoProblemNotifier({ tenantId, userId }: { tenantId: string; userId: string }) {
  const storageKey = notifyStorageKey(tenantId, userId)
  const [state, setState] = useState<NotifyState | null>(null)
  const [open, setOpen] = useState(false)
  const [soundOn, setSoundOn] = useState(true)
  // localStorage kapalıysa (gizli pencere vb.) durum bu sekmede bellekte tutulur.
  const memory = useRef<NotifyState | null>(null)
  const soundRef = useRef(true)
  const audio = useRef<AudioContext | null>(null)

  const load = useCallback((): NotifyState | null => {
    try {
      const stored = parseNotifyState(window.localStorage.getItem(storageKey))
      if (stored) return stored
    } catch {
      // depolama erişilemiyor
    }
    return memory.current
  }, [storageKey])

  const save = useCallback((next: NotifyState) => {
    memory.current = next
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next))
    } catch {
      // depolama erişilemiyor — bellekteki durum yeterli
    }
    setState(next)
  }, [storageKey])

  // Tarayıcılar sesi ancak kullanıcı etkileşiminden sonra açar: AudioContext ilk tıklama/tuşta kurulur.
  const unlockAudio = useCallback((): AudioContext | null => {
    try {
      if (!audio.current) {
        const Ctor = audioCtor()
        if (!Ctor) return null
        audio.current = new Ctor()
      }
      if (audio.current.state === 'suspended') void audio.current.resume().catch(() => {})
      return audio.current
    } catch {
      return null
    }
  }, [])

  const chime = useCallback((ctx: AudioContext | null) => {
    if (!ctx) return
    const play = () => {
      try {
        playChime(ctx)
      } catch {
        // ses çalınamadı — görsel bildirim yeterli
      }
    }
    if (ctx.state === 'running') play()
    else void ctx.resume().then(play).catch(() => {})
  }, [])

  useEffect(() => {
    try {
      const on = parseSoundPref(window.localStorage.getItem(NOTIFY_SOUND_KEY))
      setSoundOn(on)
      soundRef.current = on
    } catch {
      // varsayılan: açık
    }
    const unlock = () => {
      if (soundRef.current) unlockAudio()
    }
    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
  }, [unlockAudio])

  useEffect(() => {
    let disposed = false
    let busy = false

    const tick = async () => {
      if (busy) return
      const stored = load()
      if (!shouldFetch(stored, Date.now())) {
        setState(stored)
        return
      }
      busy = true
      try {
        const { items } = await getReadRoute<{ items: CargoProblemAlertItem[] }>('/api/admin/cargo-problem-alerts')
        if (disposed) return
        // Başka sekmede yapılan "görüldü" işaretleri kaybolmasın diye en güncel kaydın üstüne işlenir.
        const { state: next, toAlert } = applyFetch(load(), items, Date.now())
        save(next)
        if (toAlert.length > 0 && soundRef.current) chime(audio.current)
      } catch {
        // geçici hata: bir sonraki turda yeniden denenir
      } finally {
        busy = false
      }
    }

    void tick()
    // Sekme arka plandayken de sorulur: operatör başka sekmedeyken de sesli uyarı alsın.
    const intervalId = window.setInterval(() => void tick(), NOTIFY_POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick()
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) setState(parseNotifyState(e.newValue))
      if (e.key === NOTIFY_SOUND_KEY) {
        const on = parseSoundPref(e.newValue)
        setSoundOn(on)
        soundRef.current = on
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('storage', onStorage)
    return () => {
      disposed = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('storage', onStorage)
    }
  }, [storageKey, load, save, chime])

  function acknowledge(orderIds?: string[]) {
    const current = load()
    if (current) save(markSeen(current, orderIds))
  }

  function toggleSound(on: boolean) {
    setSoundOn(on)
    soundRef.current = on
    try {
      window.localStorage.setItem(NOTIFY_SOUND_KEY, on ? '1' : '0')
    } catch {
      // yalnız bu sekmede geçerli
    }
    if (on) chime(unlockAudio()) // deneme sesi (tıklama, sesi de açar)
  }

  const fresh = unseenItems(state)
  const count = fresh.length
  const countLabel = count > 99 ? '99+' : String(count)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {count > 0 ? (
          <button
            type="button"
            title={`${count} yeni sorunlu kargo`}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-destructive/40 bg-destructive/10 px-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/15 sm:px-3"
          >
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
            </span>
            <BellRing className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{countLabel} yeni sorunlu kargo</span>
            <span className="sm:hidden">{countLabel}</span>
          </button>
        ) : (
          <Button variant="ghost" size="icon" className="h-9 w-9" title="Bildirimler">
            <Bell className="h-[1.2rem] w-[1.2rem]" />
            <span className="sr-only">Bildirimler</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] max-w-[calc(100vw-2rem)] p-0">
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">Sorunlu kargo bildirimleri</p>
            <p className="text-xs text-muted-foreground">
              {count > 0 ? `${count} yeni kayıt müdahale listesinde` : 'Yeni sorunlu kargo yok'}
            </p>
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground" title="Yeni sorunlu kargo geldiğinde sesli uyarı">
            {soundOn ? <Volume2 className="h-4 w-4" aria-hidden="true" /> : <VolumeX className="h-4 w-4" aria-hidden="true" />}
            <span>Ses</span>
            <Switch checked={soundOn} onCheckedChange={toggleSound} aria-label="Sesli uyarı" />
          </label>
        </div>

        {count > 0 && (
          <ul className="max-h-80 overflow-y-auto py-1">
            {fresh.slice(0, MAX_LISTED).map((item) => (
              <li key={item.orderId}>
                <Link
                  href={cargoProblemOrderHref(item.orderId)}
                  onClick={() => {
                    acknowledge([item.orderId])
                    setOpen(false)
                  }}
                  className="block px-4 py-2 transition-colors hover:bg-accent"
                >
                  <span className="block truncate text-sm font-medium">
                    {item.fullName}
                    {item.orderNumber && <span className="font-normal text-muted-foreground"> · #{item.orderNumber}</span>}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">{item.label || 'Sorunlu kargo'}</span>
                </Link>
              </li>
            ))}
            {count > MAX_LISTED && (
              <li className="px-4 py-2 text-xs text-muted-foreground">+{count - MAX_LISTED} kayıt daha</li>
            )}
          </ul>
        )}

        <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/admin/sorunlu-kargolar?tab=mudahale" onClick={() => setOpen(false)}>
              Müdahale listesi
            </Link>
          </Button>
          {count > 0 && (
            <Button variant="outline" size="sm" onClick={() => acknowledge()}>
              Tümünü gördüm
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
